import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';

export const MIN_LENGTH_ADDRESS = 5;
export const MIN_LENGTH_PHONE = 4;
export const MIN_LENGTH_EMAIL = 5;
export const MAX_LENGTH_NAME = 255;
export const MIN_LENGTH_NAME = 2;
export const MAX_LENGTH_ADDRESS = 255;
export const MAX_LENGTH_LEGAL_ADDRESS = 500;
export const MAX_LENGTH_PHONE = 24;
export const MAX_LENGTH_EMAIL = 320;
export const MAX_LENGTH_CODE = 64;
export const MAX_LENGTH_DESCRIPTION = 1024;
export const MIN_LENGTH_DESCRIPTION = 3;
export const MAX_LENGTH_COMPANY_REGISTRATION_NUMBER = 100;
export const LENGTH_LV_REGISTRATION_NUMBER = 11;
export const MAX_LENGTH_EDUCATION_REGISTRATION_NUMBER = 50;
export const MAX_LENGTH_FACULTY = 255;
export const MIN_LENGTH_TOPIC = 2;
export const MAX_LENGTH_TOPIC = 512;
export const MINUTES_IN_HOUR = 60;
export const MIN_LENGTH_CODE = 1;
export const HOURS_IN_DAY = 24;
export const MAX_LENGTH_NOTES = 500;
export const MAX_LENGTH_DOCUMENTS_CODE = 100;
export const MAX_LENGTH_DOCUMENTS_NAME = 100;
export const MAX_LENGTH_DOCUMENT_NOTE = 255;
export const MAX_LENGTH_DOCUMENTS_NOTE = 500;
export const MAX_LENGTH_ADDITIONAL_INFO = 1000;
export const MAX_LENGTH_REJECTION_CANCELATION_REASON = 255;
export const MAX_LENGTH_INFO_FOR_CLIENT = 255;

export function structuralUnitSelectedValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const isValid = control.value && control.value !== -1;
    return isValid ? null : { structuralUnitNotSelected: true };
  };
}

export function latvianRegistrationNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const pattern = /^\d{11}$/;
    return pattern.test(control.value) ? null : { invalidRegistrationNumber: true };
  };
}

export function phoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const hasLetters = /[a-zA-Z]/.test(control.value);
    return hasLetters ? { invalidPhone: true } : null;
  };
}

export function emailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isValid = emailPattern.test(control.value);
    return isValid ? null : { invalidEmail: true };
  };
}

export const startTimeBeforeEndTimeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const startTimeStr = control.get('startTime')?.value;
  const endTimeStr = control.get('endTime')?.value;
  const isDayOff = control.get('isDayOff')?.value;

  const startTime = timeStringToMinutes(startTimeStr);
  const endTime = timeStringToMinutes(endTimeStr);

  return (startTime == null || endTime == null || endTime <= startTime) && !isDayOff ? { startTimeBeforeEndTime: true } : null;
};

function timeStringToMinutes(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }

  const [hoursStr, minutesStr] = value.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  if (isNaN(hours) || isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
}

export const requiredIfNotHolidayValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const startTimeStr = control.get('startTime')?.value;
  const endTimeStr = control.get('endTime')?.value;
  const isDayOff = control.get('isDayOff')?.value;

  const startTime = timeStringToMinutes(startTimeStr);
  const endTime = timeStringToMinutes(endTimeStr);

  return (startTime == null || endTime == null) && !isDayOff ? { requiredIfNotHoliday: true } : null;
};

export const roleStructuralUnitsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const structuralUnitIds = control.value;

  return structuralUnitIds.includes('') ? { invalidStructuralUnits: true } : null;
};

export function atLeastOneContactInfoRequired(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const email = formGroup.get('email')?.value;
    const phone = formGroup.get('phone')?.value;
    const address = formGroup.get('address')?.value;

    return email || phone || address ? null : { atLeastOneContactInfoRequired: true };
  };
}

export function onlyOneAuthFieldAllowed(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const nationalDigitalId = formGroup.get('nationalDigitalId')?.value;
    const authEmail = formGroup.get('authEmail')?.value;

    return nationalDigitalId && authEmail ? { onlyOneAuthFieldAllowed: true } : null;
  };
}

export function countryIdValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value === -1 ? { invalidCountry: true } : null;
  };
}

export function oneDayFromNowValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control.parent;
    if (!formGroup) return null;

    const visitDate = formGroup.get('visitDate')?.value;
    const visitTimeFrom = control.value;

    if (!visitDate || visitTimeFrom == null) {
      return null;
    }

    const visitDateTime = new Date(visitDate);
    const hours = Math.floor(visitTimeFrom / MINUTES_IN_HOUR);
    const minutes = visitTimeFrom % MINUTES_IN_HOUR;
    visitDateTime.setHours(hours);
    visitDateTime.setMinutes(minutes);

    const minAllowedTime = new Date();
    minAllowedTime.setHours(minAllowedTime.getHours() + HOURS_IN_DAY);
    return visitDateTime < minAllowedTime ? { oneDayFromNow: true } : null;
  };
}

export function timeRangeValidator(fromControlName: string): ValidatorFn {
  return (toControl: AbstractControl): ValidationErrors | null => {
    if (!toControl.parent) return null;

    const fromControl = toControl.parent.get(fromControlName);
    if (!fromControl || !fromControl.value || !toControl.value) return null;

    const fromTime = fromControl.value;
    const toTime = toControl.value;

    if (fromTime >= toTime) {
      return { timeRangeError: true };
    }

    return null;
  };
}

export const getControlsWithErrors = (control: AbstractControl, path: string = ''): string[] => {
  const errors: string[] = [];

  if (control instanceof FormGroup) {
    Object.entries(control.controls).forEach(([key, child]) => {
      if (key === 'longitude') return;
      const newPath = path ? `${path}.${key}` : key;
      errors.push(...getControlsWithErrors(child, newPath));
    });
  } else if (control instanceof FormArray) {
    control.controls.forEach((child) => {
      const newPath = `${path}`;
      errors.push(...getControlsWithErrors(child, newPath));
    });
  } else if (control.invalid && control.errors) {
    errors.push(path);
  }

  return errors;
};

export function getAllFormErrors(form: FormGroup | FormArray): string[] {
  const errors: string[] = [];

  Object.entries(form.controls).forEach(([key, control]) => {
    if (control instanceof FormGroup || control instanceof FormArray) {
      errors.push(...getAllFormErrors(control));
    } else if (control.errors) {
      errors.push(key);
    }
  });

  return errors;
}

export function timeFormatValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    return value === -1 ? { timeFormatError: true } : null;
  };
}

export function formatTimeWithoutSeconds(time: string | null | undefined): string {
  if (!time) return '';
  const parts = time.split(':');
  if (parts.length < 2) return time;
  return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
}

export function formatNgbDate(ngbDate: NgbDate | null): string | null {
  if (!ngbDate) {
    return null;
  }
  const year = ngbDate.year;
  const month = ngbDate.month.toString().padStart(2, '0');
  const day = ngbDate.day.toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}
